import log from "logbeacon";
import { useState } from "react";
import { appOspSecurity } from "@api/osp";
import { isEmpty } from "lodash-es";
import { pubsub } from "utils";
import { parseSSEStream } from "../utils";
import { config } from "../config";
import { UpgradeReminderModalCreateAble } from "../components/modal/UpgradeReminder";
import { useSearchEquity } from "./useSearchEquity";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";

export const useSummaryStream = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const { getCreditConfig } = useSearchEquity();
  const getAiSummary = async ({
    agentId,
    type,
    upgradeCallback,
  }: {
    agentId: string;
    type: "SUMMARY_NEWS" | "DAILY_REPORT" | "TRACKER_HEADER_SUMMARY";
    upgradeCallback?: () => void;
  }) => {
    const headers = await appOspSecurity.getRequestHeader();
    if (isEmpty(headers)) {
      open();
      return;
    }
    setIsLoading(true);
    // 重置 summary 状态，开始新的请求
    setSummary("");

    try {
      const response = await fetch(
        `${config.api.aiStream.baseUrl}/v2/ai/chat/completions?agent_id=${agentId}&type=${type}`,
        {
          method: "GET",
          headers: {
            ...headers,
            // 设置响应类型为 text/event-stream，表示期望接收 SSE 流
            "Content-Type": "text/event-stream",
          },
        },
      );

      log.debug(
        "[useSummaryStream] response status",
        response.status,
        response.ok,
      );
      log.debug("[useSummaryStream] response headers", response.headers);
      setIsLoading(false);
      setIsGenerating(true);
      // ✅ 检查响应状态
      if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        log.error(
          "[useSummaryStream] getAiSummary failed",
          response.status,
          response.statusText,
          errorText,
        );
        throw new Error(
          `HTTP ${response.status}: ${response.statusText} - ${errorText}`,
        );
      }

      // ✅ 检查响应体是否存在
      if (!response.body) {
        log.error("[useSummaryStream] response body is null");
        throw new Error("Response body is null");
      }

      // ✅ 检查 Content-Type 是否为 SSE
      const contentType = response.headers.get("Content-Type");
      log.debug("[useSummaryStream] Content-Type", contentType);
      if (contentType && !contentType.includes("text/event-stream")) {
        log.warn(
          "[useSummaryStream] Unexpected Content-Type",
          contentType,
          "Expected text/event-stream",
        );
      }

      // ✅ 累积结果用于返回（因为 setState 是异步的）
      let accumulatedSummary = "";

      // ✅ 解析 SSE 流
      for await (const chunk of parseSSEStream(response.body)) {
        accumulatedSummary += chunk;
        setSummary(accumulatedSummary);
        log.debug("[useSummaryStream] getAiSummary chunk", chunk);
      }

      log.debug(
        "[useSummaryStream] getAiSummary completed",
        accumulatedSummary,
        accumulatedSummary.length,
        "chars",
      );
      pubsub.emit(pubSubKeyMap.USER_VIEW_DAILY_SUMMARY);

      setIsGenerating(false);
      return true;
    } catch (error) {
      log.error("[useSummaryStream] getAiSummary error", error);

      setIsGenerating(false);
      setIsLoading(false);

      // ✅ 解析并设置错误信息
      const curErrorCode = error instanceof Error ? error.message : 0;
      const errorConfig = getCreditConfig?.(Number(curErrorCode));
      if (!!errorConfig) {
        upgradeCallback?.();
        await UpgradeReminderModalCreateAble({
          customConfig: errorConfig,
        });
        return;
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getAiSummary,
    isLoading,
    isGenerating,
    summary,
  };
};
